import { ComponentType } from '@angular/cdk/overlay';
import { DestroyRef, DOCUMENT, Inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { App as CapacitorApp, BackButtonListener } from '@capacitor/app';
import { Capacitor, PluginListenerHandle } from '@capacitor/core';
import { AppUpdate, AppUpdateAvailability, AppUpdateInfo } from '@capawesome/capacitor-app-update';
import { AppUpdateDialogConfig } from '@libs/shared/models';
import { catchError, from, map, Observable, of, Subject } from 'rxjs';
import { DialogService } from '../dialog';
import { EnvironmentService, MsalConfigModel } from '../environment';
import { ErrorHandlerService } from '../error-handler';
// eslint-disable-next-line no-restricted-imports -- shared-common cannot depend on the higher-level shared-services capability layer.
import { FeatureFlagsService } from '../feature-flags';
import { APP_UPDATE_DIALOG_COMPONENT_TYPE, CapacitorPlatform } from './api';

@Injectable({
  providedIn: 'root',
})
export class CapacitorService {
  private readonly onPause$: Subject<void> = new Subject();
  private readonly onResume$: Subject<void> = new Subject();
  private appUpdateInfoCache: AppUpdateInfo | undefined = undefined;
  private nativeVersion: string | undefined = undefined;

  public constructor(
    @Inject(DOCUMENT) public readonly doc: Document,
    @Inject(APP_UPDATE_DIALOG_COMPONENT_TYPE) private readonly appUpdateDialogComponentCtr: ComponentType<unknown>,
    private readonly errorHandler: ErrorHandlerService,
    private readonly envService: EnvironmentService,
    private readonly featureFlagService: FeatureFlagsService,
    private readonly dialogService: DialogService,
  ) {
  }

  public isCapacitorApp(): boolean {
    return this.getPlatform() !== 'web';
  }

  public isRunningOnIOS(): boolean {
    return this.getPlatform() === 'ios';
  }

  public isRunningOnAndroid(): boolean {
    return this.getPlatform() === 'android';
  }

  public isIOSDevice(): boolean {
    if (this.isCapacitorApp()) {
      return this.isRunningOnIOS();
    }

    const userNavigator: Navigator | undefined = this.doc.defaultView?.navigator;
    const userAgent: string = userNavigator?.userAgent ?? '';
    const maxTouchPoints: number = userNavigator?.maxTouchPoints ?? 0;

    return /iPad|iPhone|iPod/.test(userAgent) || (/Mac/.test(userAgent) && maxTouchPoints > 1);
  }

  public isAndroidDevice(): boolean {
    if (this.isCapacitorApp()) {
      return this.isRunningOnAndroid();
    }

    return /Android/.test(this.doc.defaultView?.navigator?.userAgent ?? '');
  }

  public isSafariBrowser(): boolean {
    const userAgent: string = this.doc.defaultView?.navigator?.userAgent ?? '';

    return /Safari/.test(userAgent) && !/CriOS|FxiOS|EdgiOS|OPiOS|GSA/.test(userAgent);
  }

  public getPlatform(): CapacitorPlatform {
    return Capacitor.getPlatform() as CapacitorPlatform;
  }

  public pause$(): Observable<void> {
    return this.onPause$.asObservable();
  }

  public resume$(): Observable<void> {
    return this.onResume$.asObservable();
  }

  public async initNavigationOnAndroidBackButton(destroyRef: DestroyRef): Promise<void> {
    if (!this.isRunningOnAndroid()) {
      return;
    }

    const listenerCallback: BackButtonListener = ({ canGoBack }): void => {
      if (!this.doc.defaultView) {
        return;
      }

      if (canGoBack) {
        this.doc.defaultView.history.back();
      }
    };

    const callback: PluginListenerHandle = await CapacitorApp.addListener('backButton', listenerCallback);

    const destroyCallBack = (): void => {
      void callback.remove();
    };

    destroyRef.onDestroy(destroyCallBack);
  }

  public async initNativeLifecycleEvents(destroyRef: DestroyRef): Promise<void> {
    const pause = (): void => {
      this.onPause$.next();
    };

    const resume = (): void => {
      this.onResume$.next();
    };

    if (this.isCapacitorApp()) {
      const pauseCallback: PluginListenerHandle = await CapacitorApp.addListener('pause', pause);

      const resumeCallback: PluginListenerHandle = await CapacitorApp.addListener('resume', resume);

      destroyRef.onDestroy(() => {
        void pauseCallback.remove();
        void resumeCallback.remove();
      });
    } else {
      if (!this.doc.defaultView) {
        return;
      }

      const window: Window = this.doc.defaultView;

      window.addEventListener('blur', pause);
      window.addEventListener('focus', resume);

      destroyRef.onDestroy(() => {
        window.removeEventListener('blur', pause);
        window.removeEventListener('focus', resume);
      });
    }
  }

  public async loadAppVersion(): Promise<string | undefined> {
    if (!this.isCapacitorApp()) {
      return undefined;
    }

    try {
      const result: AppUpdateInfo | undefined = await this.getAppUpdateInfo();

      if (!result) {
        return undefined;
      }

      this.nativeVersion = result.currentVersionName;

      return result.currentVersionName;
    } catch (error) {
      console.error(error);
    }

    return undefined;
  }

  public getAppVersion(): string | null {
    if (!this.nativeVersion) {
      return null;
    }

    const liveVersion: string = this.envService.appVersion();

    return `${this.nativeVersion}_${liveVersion}`;
  }

  public async startNativeAppUpdateFlow(): Promise<void> {
    if (!this.isCapacitorApp()) {
      return;
    }

    const msalConfig: MsalConfigModel = this.envService.msalConfig();

    let appStoreId: string | undefined = undefined;

    if (this.isRunningOnIOS()) {
      // ios needs the id visible in the url to the App Store page
      appStoreId = msalConfig.iosAppStoreId;
    }

    try {
      const appUpdateInfo: AppUpdateInfo | undefined = await this.getAppUpdateInfo();

      if (this.isRunningOnAndroid() && appUpdateInfo?.immediateUpdateAllowed) {
        // https://developer.android.com/guide/playcore/in-app-updates
        await AppUpdate.performImmediateUpdate();
      } else {
        await AppUpdate.openAppStore({ appId: appStoreId });
      }
    } catch (err) {
      this.errorHandler.handleError(err);
      await AppUpdate.openAppStore({ appId: appStoreId });
    }
  }

  public initNativeUpdateCheck(destroyRef: DestroyRef): void {
    if (!this.isCapacitorApp() || !this.featureFlagService.forceNativeAppUpdateEnabled()) {
      return;
    }

    this.checkForUpdateAndOpenUpdateDialog();

    this.resume$().pipe(takeUntilDestroyed(destroyRef)).subscribe(() => {
      this.appUpdateInfoCache = undefined;
      this.checkForUpdateAndOpenUpdateDialog();
    });
  }

  public checkForUpdateAndOpenUpdateDialog(): void {
    this.isNativeAppUpdateAvailable().subscribe((updateAvailable) => {
      if (!updateAvailable) {
        return;
      }

      this.dialogService.open(this.appUpdateDialogComponentCtr, {
        closeOnNavigation: false,
        disableClose: true,
        hasBackdrop: true,
      });
    });
  }

  public hasPlugins(pluginNames: string[]): boolean {
    return pluginNames.every((pluginName: string) => Capacitor.isPluginAvailable(pluginName));
  }

  // Plugins are missing when the installed native shell predates them, so the user is asked
  // to update the app before using the feature
  public arePluginsAvailable(pluginNames: string[]): boolean {
    const pluginsAvailable: boolean = this.hasPlugins(pluginNames);

    if (!pluginsAvailable) {
      this.dialogService.open(this.appUpdateDialogComponentCtr, {
        closeOnNavigation: false,
        disableClose: false,
        hasBackdrop: true,
        data: {
          title: 'Update erforderlich',
          text: 'Für diese Funktion wird die aktuelle App-Version benötigt. Bitte die App aktualisieren.',
          showCloseButton: true,
        } satisfies AppUpdateDialogConfig,
      });
    }

    return pluginsAvailable;
  }

  public isNativeAppUpdateAvailable(): Observable<boolean> {
    if (!this.featureFlagService.forceNativeAppUpdateEnabled()) {
      return of(false);
    }

    return from(this.getAppUpdateInfo())
      .pipe(
        map((data) => data?.updateAvailability === AppUpdateAvailability.UPDATE_AVAILABLE),
        catchError((err) => {
          console.error(err);
          this.errorHandler.handleError(err);

          return of(false);
        }),
      );
  }

  private async getAppUpdateInfo(): Promise<AppUpdateInfo | undefined> {
    if (this.envService.type() !== 'prod') {
      return undefined;
    }

    this.appUpdateInfoCache ??= await AppUpdate.getAppUpdateInfo({ country: 'DE' });

    return this.appUpdateInfoCache;
  }
}
